import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { FileText, FileSpreadsheet, Printer, Loader2, X, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import type { SaleWithCustomer } from '@/hooks/useSales';
import { loadSelectedSales } from '@/lib/selectedSales';
import { buildPackingSlips, type PackingSlip } from '@/lib/packingSlips';
import SalesRecords from './SalesRecords';
import SalesPackingSlips from './SalesPackingSlips';
import SalesGreetingCards from './SalesGreetingCards';
import SelectedSalesActionDock from './SelectedSalesActionDock';
import { buildGreetingCards, type GreetingCard } from '@/lib/saleGreetings';
import { usePrintPrivacy } from '@/hooks/usePrintPrivacy';

export default function SalesSelectionPanel({
  rows,
  page,
  organizationId,
  showCreator,
  disabled,
  onDelete,
  footer,
}: {
  rows: SaleWithCustomer[];
  page: number;
  organizationId: string;
  showCreator: boolean;
  disabled: boolean;
  onDelete: (sale: SaleWithCustomer) => void;
  footer?: ReactNode;
}) {
  const { can } = useAuth();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [working, setWorking] = useState<'pdf' | 'excel' | 'print' | 'greeting' | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [slips, setSlips] = useState<PackingSlip[] | null>(null);
  const [cards, setCards] = useState<GreetingCard[] | null>(null);
  const { askPrivacy, privacyDialog } = usePrintPrivacy(!disabled);
  const anchor = useRef<HTMLDivElement>(null);
  const greetingButton = useRef<HTMLButtonElement>(null);
  const operation = useRef<AbortController | null>(null);
  const printButton = useRef<HTMLButtonElement>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      operation.current?.abort();
    };
  }, []);
  const availableIds = useMemo(() => new Set(rows.map((sale) => sale.id)), [rows]);
  useEffect(() => {
    const remaining = new Set([...selected].filter((id) => availableIds.has(id)));
    if (remaining.size !== selected.size) {
      operation.current?.abort();
      setSlips(null);
      setCards(null);
      setSelected(remaining);
    }
  }, [availableIds, selected]);
  const pageRows = rows.slice(page * 50, (page + 1) * 50);
  const locked = disabled || working !== null;
  const toggle = (id: string) => {
    if (locked || !availableIds.has(id)) return;
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setError('');
  };
  const togglePage = () => {
    if (locked) return;
    setSelected((previous) => {
      const next = new Set(previous);
      const remove = pageRows.every((sale) => previous.has(sale.id));
      pageRows.forEach((sale) => {
        if (remove) next.delete(sale.id);
        else next.add(sale.id);
      });
      return next;
    });
    setError('');
  };
  async function prepare(kind: 'pdf' | 'excel' | 'print' | 'greeting') {
    if (disabled || !can('sales.view') || operation.current || !selected.size || !organizationId)
      return;
    const masked = kind === 'pdf' ? await askPrivacy() : false;
    if (masked === null || !mounted.current || disabled || operation.current) return;
    const controller = new AbortController();
    operation.current = controller;
    setWorking(kind);
    setError('');
    const active = () => mounted.current && !controller.signal.aborted;
    try {
      const documents = await loadSelectedSales(
        rows.filter((sale) => selected.has(sale.id)),
        organizationId,
        controller.signal,
        (done, total) => {
          if (active()) setProgress({ done, total });
        },
      );
      if (!active()) return;
      if (kind === 'print') {
        setSlips(buildPackingSlips(documents));
        return;
      }
      if (kind === 'greeting') {
        setCards(buildGreetingCards(documents));
        return;
      }
      const exports = await import('@/utils/selectedSalesDocuments');
      if (!active()) return;
      const file =
        kind === 'pdf'
          ? exports.buildSelectedSalesPdf(documents, masked)
          : exports.buildSelectedSalesWorkbook(documents);
      // Yield before output so pending cancellation/access changes can take effect.
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (!active()) return;
      if (kind === 'pdf')
        (file as ReturnType<typeof exports.buildSelectedSalesPdf>).save(
          `${exports.selectedSalesFileName()}.pdf`,
        );
      else
        exports.downloadSelectedSalesWorkbook(
          file as ReturnType<typeof exports.buildSelectedSalesWorkbook>,
        );
      toast.success(
        `${kind === 'pdf' ? 'PDF' : 'Excel'} download started for ${documents.length} selected sales.`,
      );
    } catch (failure) {
      if (active())
        setError(
          failure instanceof Error
            ? failure.message
            : 'Selected sales could not be prepared. Please try again.',
        );
    } finally {
      if (operation.current === controller) {
        operation.current = null;
        if (mounted.current) setWorking(null);
      }
    }
  }
  const button =
    'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border bg-background px-3 text-sm font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50';
  const actions = (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {(
        [
          ['pdf', 'Download selected PDF', 'PDF', FileText],
          ['excel', 'Download selected Excel', 'Excel', FileSpreadsheet],
          ['print', 'Print packing slips', 'Packing slips', Printer],
          ['greeting', 'Print greeting cards', 'Greetings', Heart],
        ] as const
      ).map(([kind, label, shortLabel, Icon]) => (
        <button
          key={kind}
          ref={kind === 'print' ? printButton : kind === 'greeting' ? greetingButton : undefined}
          type="button"
          aria-label={label}
          disabled={locked || !selected.size}
          onClick={() => void prepare(kind)}
          className={button}
        >
          <Icon className="h-4 w-4 shrink-0" />
          {shortLabel}
        </button>
      ))}
    </div>
  );
  const feedback = (
    <>
      {working && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p role="status" className="flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Preparing {progress.done} of {progress.total} selected sales…
          </p>
          <button type="button" onClick={() => operation.current?.abort()} className={button}>
            <X className="h-4 w-4" />
            Cancel preparation
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      )}
    </>
  );
  return (
    <div ref={anchor} className={`space-y-3 ${selected.size ? 'pb-60 sm:pb-40' : ''}`}>
      {selected.size > 0 && (
        <SelectedSalesActionDock anchor={anchor}>
          <div className="max-h-[45dvh] overflow-y-auto">
            <div className="mb-2 flex min-h-11 items-center justify-between gap-3">
              <strong aria-live="polite" className="text-sm">
                {selected.size} selected
              </strong>
              <button
                type="button"
                aria-label="Clear selected sales"
                disabled={locked}
                onClick={() => {
                  setSelected(new Set());
                  setError('');
                }}
                className="min-h-11 px-2 text-sm text-primary disabled:opacity-50"
              >
                Clear selection
              </button>
            </div>
            {actions}
            {feedback}
          </div>
        </SelectedSalesActionDock>
      )}
      <SalesRecords
        rows={pageRows}
        showCreator={showCreator}
        onDelete={onDelete}
        selection={{ ids: selected, disabled: locked, onToggle: toggle, onTogglePage: togglePage }}
      />
      {footer}
      {slips && (
        <SalesPackingSlips
          slips={slips}
          onClose={() => {
            setSlips(null);
            requestAnimationFrame(() => printButton.current?.focus());
          }}
        />
      )}
      {cards && (
        <SalesGreetingCards
          cards={cards}
          onClose={() => {
            setCards(null);
            requestAnimationFrame(() => greetingButton.current?.focus());
          }}
        />
      )}
      {privacyDialog}
    </div>
  );
}
